import { useState } from "react";
import { db, eq } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { COUNT_LABEL, useRefData } from "../../lib/refdata";
import { useSession, type Access, type Scope } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Select, Sheet, fmtDate, useToast } from "../../components/ui";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ───────────────────────────── Targets & rules ─────────────────────────────
export function SettingsRules() {
  const ref = useRefData();
  const { can } = useSession();
  const toast = useToast();
  const editable = can("settings", "edit");
  const [draft, setDraft] = useState<Record<string, any>>({});
  const val = (k: string) => draft[k] ?? ref.data?.setting(k);
  const patch = (k: string, v: any) => setDraft((d) => ({ ...d, [k]: v }));
  async function save() {
    for (const [key, value] of Object.entries(draft)) await db.update("settings", { key: eq(key) }, { value });
    setDraft({}); invalidate("ref:"); invalidate("today"); toast("Settings saved");
  }
  if (!ref.data) return <Loading />;
  const targets = val("daily_targets");
  const onDays: boolean[] = val("targets_on_days");
  const num = (k: string, path: string, label: string, hint?: string) => (
    <Field label={label}>
      <input className="input" type="number" disabled={!editable} value={val(k)?.[path] ?? ""} onChange={(e) => patch(k, { ...val(k), [path]: Number(e.target.value) })} />
      {hint && <span className="small muted">{hint}</span>}
    </Field>
  );
  const dirty = Object.keys(draft).length > 0;

  return (
    <>
      <PageHead title="Targets & rules" sub="Every number the system follows. Changes apply to Today, the scorecard and the dashboards straight away."
        actions={editable && <button className="btn primary" disabled={!dirty} onClick={save}>{dirty ? "Save changes" : "Saved"}</button>} />
      <div style={{ display: "grid", gap: 14 }}>
        <Panel title="Daily targets" hint="The six counts, by day of the week">
          <div className="panel-body table-wrap">
            <table className="data">
              <thead><tr><th />{DAYS.map((d) => <th key={d} className="num">{d}</th>)}</tr></thead>
              <tbody>
                {Object.keys(COUNT_LABEL).map((c) => (
                  <tr key={c} style={{ cursor: "default" }}>
                    <td>{COUNT_LABEL[c]}</td>
                    {DAYS.map((_, i) => (
                      <td key={i} className="num"><input className="input" style={{ width: 56, textAlign: "right" }} type="number" min={0} disabled={!editable}
                        value={targets[c][i]} onChange={(e) => { const t = structuredClone(targets); t[c][i] = Number(e.target.value); patch("daily_targets", t); }} /></td>
                    ))}
                  </tr>
                ))}
                <tr style={{ cursor: "default" }}>
                  <td>Targets on this day</td>
                  {DAYS.map((_, i) => <td key={i} className="num"><input type="checkbox" disabled={!editable} checked={onDays[i]} onChange={(e) => { const o = [...onDays]; o[i] = e.target.checked; patch("targets_on_days", o); }} /></td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>
        <Panel title="Check-in rhythm" hint="Check in every … days">
          <div className="panel-body form-grid">
            {Object.keys(val("rhythm_bond")).map((b) => num("rhythm_bond", b, b))}
            {num("rhythm_area", "hnw", "HNW prospects")}
            {num("rhythm_area", "hnw_nurture", "HNW on Nurture")}
            {num("rhythm_area", "outreach", "Warm outreach")}
            {num("rhythm_area", "travel", "Travel clients")}
            {val("travel_prospecting") && <>
              <Field label="Travel buyers to reach out to"><select className="select" disabled={!editable} value={val("travel_prospecting").every ?? "week"}
                onChange={(e) => patch("travel_prospecting", { ...val("travel_prospecting"), every: e.target.value })}>
                <option value="week">Each week (made on Monday)</option><option value="day">Each day</option><option value="off">Off</option></select></Field>
              <Field label="How many each time"><input className="input" type="number" min={0} disabled={!editable} value={val("travel_prospecting").count ?? 10}
                onChange={(e) => patch("travel_prospecting", { ...val("travel_prospecting"), count: Number(e.target.value) })} /></Field>
            </>}
            {num("rhythm_area", "new_connections", "New connections")}
            {num("rhythm_area", "team", "Team: expect a move every")}
          </div>
        </Panel>
        <Panel title="Never an irritant" hint="Guardrails that protect relationships">
          <div className="panel-body form-grid">
            {num("guardrails", "min_days_between_unanswered", "Days between unanswered touches")}
            {num("guardrails", "unanswered_before_pause", "Unanswered in a row before a pause")}
            {num("guardrails", "pause_days", "Pause length (days)")}
            {num("guardrails", "gives_before_ask", "Gives before an ask")}
            {num("guardrails", "special_days_ahead", "Show birthdays this many days ahead")}
            {num("warm_picks", "auto", "Today's 10: picked for you")}
            {num("warm_picks", "mine", "Today's 10: picked by you")}
            {num("warm_picks", "skip_rest_days", "Rest after “Not now” (days)")}
          </div>
        </Panel>
        <Panel title="Response rules">
          <div className="panel-body form-grid">
            {num("response_rules", "met_message_hours", "Message people you met within (hours)")}
            {num("response_rules", "inbound_reply_hours", "Reply to people who reach out within (hours)")}
            {num("response_rules", "travel_remind_before_end_days", "Travel: remind before coverage ends (days)")}
            {num("response_rules", "pipeline_stale_days", "Pipeline: stale after (days)")}
          </div>
        </Panel>
        <Panel title="Team check-in health" hint="Days since last check-in">
          <div className="panel-body form-grid">
            <Field label="Not licensed: green within"><input className="input" type="number" disabled={!editable} value={val("team_contact_health").not_licensed.green} onChange={(e) => { const h = structuredClone(val("team_contact_health")); h.not_licensed.green = Number(e.target.value); patch("team_contact_health", h); }} /></Field>
            <Field label="Not licensed: amber up to"><input className="input" type="number" disabled={!editable} value={val("team_contact_health").not_licensed.amber} onChange={(e) => { const h = structuredClone(val("team_contact_health")); h.not_licensed.amber = Number(e.target.value); patch("team_contact_health", h); }} /></Field>
            <Field label="Licensed: green within"><input className="input" type="number" disabled={!editable} value={val("team_contact_health").licensed.green} onChange={(e) => { const h = structuredClone(val("team_contact_health")); h.licensed.green = Number(e.target.value); patch("team_contact_health", h); }} /></Field>
            <Field label="Licensed: amber up to"><input className="input" type="number" disabled={!editable} value={val("team_contact_health").licensed.amber} onChange={(e) => { const h = structuredClone(val("team_contact_health")); h.licensed.amber = Number(e.target.value); patch("team_contact_health", h); }} /></Field>
          </div>
        </Panel>
        <Panel title="Your week" hint="Focus for each day, shown at the top of Today">
          <div className="panel-body" style={{ display: "grid", gap: 10 }}>
            {(val("weekly_focus") as any[]).map((d, i) => (
              <Field key={i} label={DAYS[i]}>
                <input className="input" disabled={!editable} value={d.focus} onChange={(e) => { const w = structuredClone(val("weekly_focus")); w[i].focus = e.target.value; patch("weekly_focus", w); }} />
              </Field>
            ))}
          </div>
        </Panel>
      </div>
    </>
  );
}

// ───────────────────────────── Lists ─────────────────────────────
export function SettingsLists() {
  const ref = useRefData();
  const { can } = useSession();
  const [key, setKey] = useState("Channels");
  const [newVal, setNewVal] = useState("");
  const editable = can("settings", "edit");
  if (!ref.data) return <Loading />;
  const keys = [...new Set(ref.data.raw.lists.map((l) => l.list_key))].sort();
  const items = ref.data.raw.lists.filter((l) => l.list_key === key);
  async function add() {
    if (!newVal.trim()) return;
    await db.insert("list_items", { list_key: key, value: newVal.trim(), sort: (items.at(-1)?.sort ?? 0) + 10 });
    setNewVal(""); invalidate("ref:");
  }
  async function toggle(v: string, active: boolean) {
    await db.update("list_items", { list_key: eq(key), value: eq(v) }, { is_active: active });
    invalidate("ref:");
  }
  return (
    <>
      <PageHead title="Lists" sub="The choices in every dropdown. Hide ones you don't use; nothing already recorded changes." />
      <div className="today-cols">
        <Panel title={key}>
          <ul className="task-list">
            {items.map((i) => (
              <li key={i.value} className="task">
                <span style={{ color: i.is_active ? undefined : "var(--ink-3)" }}>{i.value}</span>
                {editable && <div className="task-actions"><button className="btn small ghost" onClick={() => toggle(i.value, !i.is_active)}>{i.is_active ? "Hide" : "Show"}</button></div>}
              </li>
            ))}
          </ul>
          {editable && (
            <div className="panel-body" style={{ display: "flex", gap: 8 }}>
              <input className="input" value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder={`Add to ${key}`} onKeyDown={(e) => e.key === "Enter" && add()} />
              <button className="btn" onClick={add}>Add</button>
            </div>
          )}
        </Panel>
        <Panel title="Choose a list">
          <ul className="task-list">
            {keys.map((k) => <li key={k} className="task"><button className="name" style={{ fontWeight: k === key ? 700 : 500 }} onClick={() => setKey(k)}>{k}</button></li>)}
          </ul>
        </Panel>
      </div>
    </>
  );
}

// ───────────────────────────── Users & permissions ─────────────────────────────
export function SettingsUsers() {
  const { can, profile } = useSession();
  const toast = useToast();
  const editable = can("users", "edit");
  const data = useQuery("users:all", async () => {
    const [profiles, invites, roles, modules, perms, members, admins] = await Promise.all([
      db.select("profiles", { select: "*", order: "display_name" }),
      db.select("invites", { select: "*", accepted_at: "is.null", order: "created_at.desc" }).catch(() => []),
      db.select("roles", { select: "*", order: "sort" }),
      db.select("modules", { select: "*", order: "sort" }),
      db.select("role_permissions", { select: "*" }),
      db.select("team_overview", { select: "member_id,full_name,agent_code", status: "eq.Active", order: "full_name" }).catch(() => []),
      db.rpc<string[]>("people_admins").catch(() => [] as string[]),
    ]);
    return { profiles, invites, roles, modules: modules.filter((m: any) => m.key !== "people_admin"), perms, members, admins: new Set<string>(admins) };
  });
  const [inviting, setInviting] = useState(false);
  const [role, setRole] = useState("assistant");
  const isOwner = profile?.role_key === "owner";

  if (data.loading && !data.data) return <Loading />;
  if (!data.data) return <ErrorBox error={data.error} />;
  const d = data.data;
  const permOf = (r: string, m: string) => d.perms.find((p: any) => p.role_key === r && p.module === m) ?? { access: "none", scope: "own" };
  async function setPerm(r: string, m: string, patch: { access?: Access; scope?: Scope }) {
    await db.upsert("role_permissions", { role_key: r, module: m, ...permOf(r, m), ...patch }, "role_key,module");
    invalidate("users:all"); toast("Permission updated");
  }

  return (
    <>
      <PageHead title="Users & permissions" sub="Invite people, choose their role, and decide what each role can see and change."
        actions={editable && <button className="btn primary" onClick={() => setInviting(true)}>Invite someone</button>} />
      <div style={{ display: "grid", gap: 14 }}>
        <Panel title="People with access">
          <table className="data">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th title="Can remove people and merge duplicates">Remove &amp; merge</th></tr></thead>
            <tbody>
              {d.profiles.map((p: any) => (
                <tr key={p.user_id} style={{ cursor: "default" }}>
                  <td>{isOwner || p.user_id === profile?.user_id ? <NameCell p={p} me={p.user_id === profile?.user_id} /> : <strong>{p.display_name}</strong>}</td>
                  <td>{p.email}</td>
                  <td>{editable && p.role_key !== "owner" ? (
                    <Select value={p.role_key} onChange={async (v) => { await db.update("profiles", { user_id: eq(p.user_id) }, { role_key: v }); invalidate("users:all"); toast("Role changed"); }}
                      options={d.roles.filter((r: any) => r.key !== "owner").map((r: any) => ({ value: r.key, label: r.label }))} />
                  ) : d.roles.find((r: any) => r.key === p.role_key)?.label}</td>
                  <td>{p.role_key !== "owner" && editable ? (
                    <input type="checkbox" checked={p.is_active} onChange={async (e) => { await db.update("profiles", { user_id: eq(p.user_id) }, { is_active: e.target.checked }); invalidate("users:all"); }} />
                  ) : "Yes"}</td>
                  <td>{p.role_key === "owner" ? <span className="small muted">Always</span> : isOwner ? (
                    <input type="checkbox" aria-label={`${p.display_name} can remove and merge people`} checked={d.admins.has(p.user_id)} onChange={async (e) => {
                      await db.rpc("set_people_admin", { p_user: p.user_id, p_on: e.target.checked }); invalidate("users:all"); toast(e.target.checked ? `${p.display_name} can now remove and merge people` : "Permission removed");
                    }} />) : d.admins.has(p.user_id) ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {d.invites.length > 0 && (
            <div className="panel-body">
              <p className="small muted">Waiting for these people to create their account. Each one gets an email with the link and steps.</p>
              {d.invites.map((i: any) => <div key={i.email} className="small">{i.display_name} · {i.email} · {d.roles.find((r: any) => r.key === i.role_key)?.label} · invited {fmtDate(i.created_at)}
                {i.emailed_at ? ` · emailed ${fmtDate(i.emailed_at)}` : ""}
                {editable && <button className="btn small ghost" onClick={async () => { await db.rpc("invite_resend", { p_email: i.email }); invalidate("users:all"); toast(`Invite emailed again to ${i.email}`); }}>Resend email</button>}
                {editable && <button className="btn small ghost danger" onClick={async () => { await db.remove("invites", { email: eq(i.email) }); invalidate("users:all"); }}>Cancel invite</button>}</div>)}
            </div>
          )}
        </Panel>

        <Panel title="What each role can do" hint="Owner always has full access">
          <div className="panel-body">
            <div className="toolbar">
              <span className="small muted">Role:</span>
              <div className="seg">{d.roles.filter((r: any) => r.key !== "owner").map((r: any) => (
                <button key={r.key} aria-pressed={role === r.key} onClick={() => setRole(r.key)}>{r.label}</button>
              ))}</div>
            </div>
            <table className="data">
              <thead><tr><th>Area</th><th>Access</th><th>Whose records</th></tr></thead>
              <tbody>
                {d.modules.map((m: any) => {
                  const p = permOf(role, m.key);
                  return (
                    <tr key={m.key} style={{ cursor: "default" }}>
                      <td>{m.label}</td>
                      <td><div className="seg">{(["none", "view", "edit"] as Access[]).map((a) => (
                        <button key={a} aria-pressed={p.access === a} disabled={!editable} onClick={() => setPerm(role, m.key, { access: a })}>{a === "none" ? "No access" : a === "view" ? "View" : "Edit"}</button>
                      ))}</div></td>
                      <td><div className="seg">{(["own", "downline", "all"] as Scope[]).map((s) => (
                        <button key={s} aria-pressed={p.scope === s} disabled={!editable || p.access === "none"} onClick={() => setPerm(role, m.key, { scope: s })}>{s === "own" ? "Their own" : s === "downline" ? "Their downline" : "Everyone's"}</button>
                      ))}</div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
      {inviting && <InviteSheet roles={d.roles} members={d.members} onClose={() => setInviting(false)} />}
    </>
  );
}

function NameCell({ p, me }: { p: any; me: boolean }) {
  const { refresh } = useSession();
  const toast = useToast();
  const [v, setV] = useState<string | null>(null);
  const [err, setErr] = useState("");
  async function save() {
    if (v == null || v.trim() === p.display_name) { setV(null); return; }
    const r = await db.rpc<{ ok: boolean; reason?: string }>("set_display_name", { p_user: p.user_id, p_name: v });
    if (!r.ok) { setErr(r.reason ?? "Not saved"); return; }
    setV(null); setErr(""); invalidate("users:all"); if (me) await refresh(); toast("Name updated");
  }
  if (v == null) return <span className="name-cell"><strong>{p.display_name}</strong>{me && <span className="muted small"> (you)</span>}
    <button className="btn small ghost" onClick={() => setV(p.display_name)} aria-label={`Edit name of ${p.display_name}`}>Edit</button></span>;
  return (
    <span className="name-cell">
      <input className="input" autoFocus value={v} maxLength={80} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setV(null); }} style={{ maxWidth: 200 }} />
      <button className="btn small primary" onClick={save}>Save</button>
      {err && <span className="small" style={{ color: "var(--alert)" }}>{err}</span>}
    </span>
  );
}

function InviteSheet({ roles, members, onClose }: { roles: any[]; members: any[]; onClose: () => void }) {
  const toast = useToast();
  const [f, setF] = useState<Record<string, string>>({ role_key: "assistant" });
  const [error, setError] = useState<Error | null>(null);
  async function save() {
    try {
      if (!f.email || !f.display_name) throw new Error("Add their name and email.");
      await db.insert("invites", { email: f.email.trim().toLowerCase(), display_name: f.display_name, role_key: f.role_key, team_member_id: f.team_member_id || null });
      invalidate("users:all");
      toast(`Invited ${f.display_name} · they'll get an email with the link and steps`);
      onClose();
    } catch (e) { setError(e as Error); }
  }
  return (
    <Sheet title="Invite someone" onClose={onClose} footer={<button className="btn primary" onClick={save}>Send invite</button>}>
      <ErrorBox error={error} />
      <div className="form-grid">
        <Field label="Name"><input className="input" value={f.display_name ?? ""} onChange={(e) => setF({ ...f, display_name: e.target.value })} /></Field>
        <Field label="Email"><input className="input" type="email" value={f.email ?? ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Role"><Select value={f.role_key} onChange={(v) => setF({ ...f, role_key: v })} options={roles.filter((r) => r.key !== "owner").map((r) => ({ value: r.key, label: r.label }))} /></Field>
        <Field label="Their team record (for downline access)"><Select value={f.team_member_id} onChange={(v) => setF({ ...f, team_member_id: v })} options={members.map((m) => ({ value: m.member_id, label: `${m.full_name}${m.agent_code ? ` (${m.agent_code})` : ""}` }))} placeholder="Not on the team" /></Field>
      </div>
      <p className="small muted">They create their account on this site with this email. Until then they can't see anything.</p>
    </Sheet>
  );
}

export function NoAccess() {
  return <Empty title="You don't have access to this page">Ask the owner to change your permissions.</Empty>;
}

// ───────────────────────────── Milestones ─────────────────────────────
type MDef = { key: string; label: string; due_label: string | null; due_days: number | null; is_count: boolean; in_progress: boolean; sort: number; is_active: boolean };

export function SettingsMilestones() {
  const { can } = useSession();
  const toast = useToast();
  const editable = can("settings", "edit");
  const defs = useQuery("ref:milestones:all", () => db.select<MDef>("milestone_defs", { select: "*", order: "sort" }));
  const [edit, setEdit] = useState<Partial<MDef> & { isNew?: boolean } | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const rows = defs.data ?? [];
  const counted = rows.filter((r) => r.is_active && r.in_progress).length;

  async function save(patch: Partial<MDef>, key: string) {
    await db.update("milestone_defs", { key: eq(key) }, patch);
    invalidate("ref:"); invalidate("team"); invalidate("dash:team");
  }
  async function toggle(r: MDef) {
    const on = !r.is_active;
    await save({ is_active: on, in_progress: on && !r.is_count }, r.key);
    toast(on ? `${r.label} switched on` : `${r.label} switched off`);
  }
  async function move(r: MDef, dir: -1 | 1) {
    const i = rows.indexOf(r), j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const other = rows[j];
    await Promise.all([save({ sort: other.sort }, r.key), save({ sort: r.sort === other.sort ? r.sort + dir : r.sort }, other.key)]);
  }
  async function submit() {
    if (!edit) return;
    setErr(null);
    try {
      const label = (edit.label ?? "").trim();
      if (!label) throw new Error("Give the milestone a name.");
      const body = { label, due_label: edit.due_label?.trim() || null, due_days: edit.due_days === null || edit.due_days === undefined || (edit.due_days as any) === "" ? null : Number(edit.due_days), is_count: !!edit.is_count };
      if (edit.isNew) {
        const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || `step_${Date.now()}`;
        if (rows.some((r) => r.key === key)) throw new Error("A milestone with that name already exists.");
        await db.insert("milestone_defs", { key, ...body, in_progress: !body.is_count, is_active: true, sort: (rows.at(-1)?.sort ?? 0) + 10 });
      } else await save({ ...body, in_progress: edit.is_active ? !body.is_count : false }, edit.key!);
      invalidate("ref:"); invalidate("team"); invalidate("dash:team");
      setEdit(null); toast("Milestone saved");
    } catch (e) { setErr(e as Error); }
  }

  return (
    <>
      <PageHead title="Milestones" sub="The onboarding steps on the Recruit Tracker. Switch them on or off, add new ones, rename, reorder and set the due window."
        actions={editable && <button className="btn primary" onClick={() => setEdit({ isNew: true, label: "", due_label: "", due_days: null, is_count: false })}>Add milestone</button>} />
      <ErrorBox error={defs.error} />
      <Panel title={`${counted} milestones count towards progress`} hint="Switching one off hides it from the tracker and dashboards. Ticks already recorded are kept and come back if you switch it on again.">
        {!defs.data ? <Loading /> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th style={{ width: 70 }}>Order</th><th>Milestone</th><th>Due window</th><th className="num">Due by (days)</th><th>Type</th><th>Status</th><th /></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key} style={{ opacity: r.is_active ? 1 : 0.55 }}>
                    <td className="nowrap">{editable && <>
                      <button className="icon-btn" disabled={i === 0} onClick={() => move(r, -1)} aria-label="Move up">↑</button>
                      <button className="icon-btn" disabled={i === rows.length - 1} onClick={() => move(r, 1)} aria-label="Move down">↓</button></>}</td>
                    <td><b style={{ color: "var(--navy)" }}>{r.label}</b></td>
                    <td>{r.due_label ?? <span className="muted">—</span>}</td>
                    <td className="num">{r.due_days ?? <span className="muted">—</span>}</td>
                    <td>{r.is_count ? "Number (e.g. workshops)" : "Tick ✓"}</td>
                    <td>{editable ? (
                      <label className="switch"><input type="checkbox" checked={r.is_active} onChange={() => toggle(r)} /><span />{r.is_active ? "On" : "Off"}</label>
                    ) : r.is_active ? "On" : "Off"}</td>
                    <td>{editable && <button className="btn small ghost" onClick={() => setEdit({ ...r })}>Edit</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {edit && (
        <Sheet title={edit.isNew ? "Add a milestone" : `Edit ${edit.label}`} onClose={() => setEdit(null)}
          footer={<><button className="btn" onClick={() => setEdit(null)}>Cancel</button><button className="btn primary" onClick={submit}>Save</button></>}>
          <ErrorBox error={err} />
          <div className="form-grid">
            <Field label="Name" wide><input className="input" value={edit.label ?? ""} onChange={(e) => setEdit({ ...edit, label: e.target.value })} placeholder="e.g. Launch" /></Field>
            <Field label="Due window (as shown on the tracker)"><input className="input" value={edit.due_label ?? ""} onChange={(e) => setEdit({ ...edit, due_label: e.target.value })} placeholder="e.g. Day 3-7" /></Field>
            <Field label="Overdue after (days from recruit date)"><input className="input" type="number" min={0} value={edit.due_days ?? ""} onChange={(e) => setEdit({ ...edit, due_days: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
            <label className="check wide"><input type="checkbox" checked={!!edit.is_count} onChange={(e) => setEdit({ ...edit, is_count: e.target.checked })} /> This is a number, not a tick (like Workshop Nos). Numbers don't count towards progress.</label>
          </div>
        </Sheet>
      )}
    </>
  );
}

const ROUTE_KINDS: { key: string; label: string; hint: string; allowed: string[] }[] = [
  { key: "follow_up", label: "Promised follow-ups", hint: "A follow-up date set when logging a conversation", allowed: ["logger", "owner", "assistant", "record"] },
  { key: "commitment", label: "Commitments past due", hint: "What someone said they'd do, now late", allowed: ["logger", "owner", "recruiter", "assistant"] },
  { key: "licensing", label: "9-month licensing deadlines", hint: "Unlicensed agents within 30 days of their mark", allowed: ["owner", "recruiter", "admin", "assistant"] },
  { key: "team_checkin", label: "Team check-ins", hint: "Members who are red or never checked in", allowed: ["owner", "admin", "assistant"] },
  { key: "tracker", label: "Recruit tracker behind", hint: "Tracker steps past their due date", allowed: ["owner", "admin", "assistant"] },
  { key: "pipeline", label: "Pipeline next actions", hint: "Cards with no next action, late, or booked but not scheduled", allowed: ["record", "owner", "assistant"] },
  { key: "reviews", label: "Client reviews to prepare and book", hint: "Overdue and due within 30 days", allowed: ["assistant", "owner", "admin"] },
  { key: "touch_base", label: "Client touch-bases", hint: "A friendly check-in, no agenda", allowed: ["assistant", "owner", "admin"] },
  { key: "policy", label: "Policy dates and turning 18", hint: "Renewals, conversions and expiries within 30 days; children turning 18 within 90 days", allowed: ["owner", "assistant", "admin"] },
  { key: "birthday", label: "Birthdays today", hint: "Send the wish (the parent for a child)", allowed: ["assistant", "owner", "everyone"] },
  { key: "meeting", label: "Today's meetings", hint: "Shown to whoever booked them", allowed: ["record", "owner", "assistant"] },
];
const ROUTE_LABEL: Record<string, string> = {
  owner: "You (the account owner)", logger: "Whoever logged it", record: "Whoever owns the record", recruiter: "The member's recruiter (if they use the CRM)",
  assistant: "Assistant queue", admin: "Admin queue", everyone: "Everyone (on each user's list)",
};

/** Settings → Who does what: the owner of each kind of Command Center item. */
export function SettingsRouting() {
  const ref = useRefData();
  const toast = useToast();
  const { can } = useSession();
  const cur = ref.data?.setting("routing") ?? {};
  const [f, setF] = useState<Record<string, any> | null>(null);
  const v = f ?? cur;
  const [err, setErr] = useState<Error | null>(null);
  const editable = can("settings", "edit");
  async function save() {
    try { await db.update("settings", { key: eq("routing") }, { value: { ...cur, ...v } }); invalidate("ref:"); invalidate("cc"); setF(null); toast("Saved: lists update right away"); }
    catch (e) { setErr(e as Error); }
  }
  return (
    <>
      <PageHead title="Who does what" sub="Every automatic item in the Command Center has one owner. Pick who. A queue with nobody in it comes to you, so nothing is ever dropped."
        actions={editable && f && <button className="btn primary" onClick={save}>Save changes</button>} />
      <ErrorBox error={err} />
      <Panel>
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Item</th><th>Goes to</th></tr></thead>
            <tbody>
              {ROUTE_KINDS.map((k) => (
                <tr key={k.key}>
                  <td><strong style={{ color: "var(--navy)" }}>{k.label}</strong><div className="small muted">{k.hint}</div></td>
                  <td style={{ minWidth: 260 }}><Select disabled={!editable} value={v[k.key] ?? "owner"} onChange={(x) => setF({ ...v, [k.key]: x || "owner" })}
                    options={k.allowed.map((a) => ({ value: a, label: ROUTE_LABEL[a] }))} /></td>
                </tr>
              ))}
              <tr>
                <td><strong style={{ color: "var(--navy)" }}>Escalate to you after</strong><div className="small muted">Anything someone else owns that is this late also appears on your list, with their name</div></td>
                <td><Select disabled={!editable} value={String(v.escalate_after_days ?? 3)} onChange={(x) => setF({ ...v, escalate_after_days: Number(x) || 3 })}
                  options={[1, 2, 3, 5, 7].map((n) => ({ value: String(n), label: `${n} day${n > 1 ? "s" : ""} late` }))} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
